<template>
  <section class="message">
    <div v-if="isGetRequestPending"><ms-loading :size="'large'" :justify="true"></ms-loading></div>

    <div v-else-if="!!item">
      <div v-if="isGenerateRequestCompleted" class="generate-success">
        <h3>Message generated successfully</h3>
        <div class="action-list">
          <ms-button @click="close">Close</ms-button>
          <ms-button color="primary" @click="cancel">Home</ms-button>
        </div>

        <ms-toast v-if="warnings && warnings.length" type="warning">
          <div class="validation-result">
            Removed following recipients because they have failed <b>ClientGuard</b> validation:

            <ul class="validation-result-list">
              <li v-for="warning of warnings" :key="warning.emailAddress + warning.fieldName">
                <b>{{ warning.emailAddress }}</b> from
                <b
                  ><i>{{ warning.fieldName.toUpperCase() }}</i></b
                >
                field;
              </li>
            </ul>
          </div>
        </ms-toast>
        <ms-toast v-else type="success">Message was successfully validated by <b>ClientGuard</b>.</ms-toast>
      </div>
      <div v-else>
        <h2>{{ item.name }}</h2>

        <div v-if="item.updatedAt" class="message-meta">updated by {{ item.updatedBy }} on {{ new Date(item.updatedAt).toLocaleString() }}</div>
        <div v-else class="message-meta">created by {{ item.createdBy }} on {{ new Date(item.createdAt).toLocaleString() }}</div>
        <p>{{ item.description }}</p>
        <form @submit.prevent.stop="generate">
          <ul class="param-list">
            <li v-for="field of form" :key="field.name" class="param-list-item form-group">
              <ms-text-field v-model="field.value" :label="field.label" :disabled="isGenerateRequestPending"></ms-text-field>
            </li>
            <li class="form-group">
              <ms-checkbox v-model="skipCache" label="Bypass cache" :disabled="isGenerateRequestPending"></ms-checkbox>
            </li>
            <li v-if="isGenerateRequestFailed" class="form-group">
              <ms-toast type="error"> Failed to generate a message. Please, try again later or contact the development team. </ms-toast>
            </li>
          </ul>

          <div class="form-group submit">
            <ms-button type="button" :disabled="isGenerateRequestPending" @click="cancel">Back</ms-button>
            <ms-button :disabled="!isValid || isGenerateRequestPending" :color="'primary'"> Generate</ms-button>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import logger from '@/utils/logger';
import MsTextField from './../ms/TextField';
import MsToast from './../ms/Toast';
import MsButton from './../ms/Button';
import MsCheckbox from './../ms/Checkbox';
import MsLoading from './../ms/Loading';

import { applyToCompose } from './../../utils/office';

export default {
  components: {
    'ms-text-field': MsTextField,
    'ms-toast': MsToast,
    'ms-button': MsButton,
    'ms-checkbox': MsCheckbox,
    'ms-loading': MsLoading
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      skipCache: false,
      form: []
    };
  },
  computed: {
    ...mapState({
      item: s => s.messages.item,

      warnings: s => s.messages.warnings,
      message: s => s.messages.message,

      isGetRequestPending: s => s.messages.isGetRequestPending,

      isGenerateRequestPending: s => s.messages.isGenerateRequestPending,
      isGenerateRequestCompleted: s => s.messages.isGenerateRequestCompleted,
      isGenerateRequestFailed: s => s.messages.isGenerateRequestFailed
    }),
    isValid() {
      for (const item of this.form) {
        if (!item.value || !item.value.length) {
          return false;
        }
      }
      return true;
    }
  },
  watch: {
    item() {
      this.form =
        this.item && this.item.params
          ? this.item.params.map(({ name, label }) => ({
              name,
              label,
              value: ''
            }))
          : [];
    }
  },
  async created() {
    await this.$store.dispatch('messages/initialize', this.id);
    await this.$store.dispatch('messages/getById', this.id);
  },
  async mounted() {
    this.$trackEvent('Template page opened');
  },
  methods: {
    async generate() {
      if (!this.isValid) {
        return;
      }
      const lock = this.$lock();
      try {
        this.$trackEvent(`Message generated`, { id: this.item.id, params: this.item.params });

        await this.$store.dispatch('messages/generate', {
          templateId: this.id,
          skipCache: this.skipCache,
          params: this.form
        });
        await applyToCompose(this.message);
      } catch (e) {
        logger.error(e);
      } finally {
        lock.release();
      }
    },
    async cancel() {
      this.$trackEvent(`Back to list`);
      await this.$store.dispatch('navigation/push', {
        path: '/'
      });
    },
    async close() {
      this.$trackEvent('Pane closed');

      // eslint-disable-next-line no-undef
      Office.context.ui.closeContainer();
    }
  }
};
</script>

<style lang="scss" scoped>
.message {
  padding: 0.5rem 1rem;
}
.message-meta {
  font-size: 0.75rem;
  color: rgb(161, 159, 157);
}
.param-list {
  display: block;
  list-style: none;
  margin: 0;
  padding: 0;

  .param-list-item {
    margin: 0;
    padding: 0;
  }
}

.form-group {
  margin-bottom: 1rem;

  &.param-list-item {
    margin-bottom: 1rem;
  }

  &.submit {
    display: flex;
    justify-content: space-around;
    margin-bottom: 0;
  }
}

.generate-success {
  margin: 25% auto;
  display: block;
  width: 100%;
  text-align: center;
  .action-list {
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;

    > * {
      &:not(:last-child) {
        margin-right: 1rem;
      }
    }
  }

  .validation-result {
    text-align: left;
    .validation-result-list {
      padding: 0;
    }
  }
}
</style>
